import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Star } from "lucide-react";
import api from "@/lib/api";
import { LoadingState } from "@/components/ui/States";
import NotificationRoutingCard from "@/components/admin/NotificationRoutingCard";

// Rows John was specifically asked to initial before launch. Highlighted
// with a yellow star + a "John initial me first" hint when they're still
// un-verified. Both keys come from /app/backend/services/seed.py.
const PRIORITY_KEYS = new Set([
    "service_hours",
    "approve_phase3_placeholders",
]);

export default function VerificationChecklist() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        api.get("/admin/verification-checklist").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const update = async (item, value, initialed) => {
        await api.patch("/admin/verification-checklist", {
            key: item.key,
            value,
            initialed,
        });
        load();
    };

    if (loading) return <LoadingState />;

    const initialedCount = items.filter((i) => i.initialed).length;
    const pendingCount = items.length - initialedCount;
    const priorityItems = items.filter((i) => PRIORITY_KEYS.has(i.key));
    const allPriorityDone = priorityItems.length > 0 && priorityItems.every((i) => i.initialed);

    return (
        <div data-testid="verification-checklist">
            <h1 className="mb-2">Pre-Publish Verification Sheet</h1>
            <p className="text-sm text-[var(--color-text-muted)] mb-2">
                <strong>{initialedCount}</strong> of <strong>{items.length}</strong> verified
                {pendingCount > 0 ? <> · <strong style={{ color: "var(--color-warning)" }}>{pendingCount} still pending</strong></> : null}.
                Items must be initialed before public schema (reviews, license, warranty) is allowed to emit.
            </p>

            {/* Priority callout — Service Hours + Phase 3 Placeholders Approved */}
            {priorityItems.length > 0 ? (
                <div
                    className="a1-card mb-5 flex items-start gap-3"
                    style={{
                        background: allPriorityDone ? "rgba(40, 216, 224, 0.08)" : "rgba(255, 214, 95, 0.08)",
                        borderColor: allPriorityDone ? "var(--color-accent)" : "var(--color-warning)",
                    }}
                    data-testid="verification-priority-callout"
                >
                    {allPriorityDone ? (
                        <CheckCircle2 size={22} className="text-[var(--color-accent)] shrink-0 mt-0.5" />
                    ) : (
                        <Star size={22} className="text-[var(--color-warning)] shrink-0 mt-0.5" />
                    )}
                    <div>
                        <p className="font-bold m-0 mb-1">
                            {allPriorityDone ? "Priority rows already verified" : "John — initial these first"}
                        </p>
                        <ul className="text-sm m-0 list-disc pl-5 leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                            {priorityItems.map((i) => (
                                <li key={i.key} data-testid={`verification-priority-${i.key}`}>
                                    <strong>{i.label}</strong>{" "}
                                    {i.initialed ? (
                                        <span style={{ color: "var(--color-accent)" }}>— verified{i.initialed_by ? ` by ${i.initialed_by}` : ""}.</span>
                                    ) : (
                                        <span style={{ color: "var(--color-warning)" }}>— scroll to the row below and check the box.</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            ) : null}

            <NotificationRoutingCard />
            <div className="a1-card">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left border-b border-[var(--color-line)]">
                            <th className="py-2">Item</th>
                            <th className="py-2 hidden sm:table-cell">Value</th>
                            <th className="py-2 text-center">Verified</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <Row
                                key={item.key}
                                item={item}
                                priority={PRIORITY_KEYS.has(item.key)}
                                onSave={update}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Row({ item, priority, onSave }) {
    const [value, setValue] = useState(item.value || "");
    const [saving, setSaving] = useState(false);

    // Visual cue for un-initialed rows. Priority rows get a stronger yellow
    // halo so John can find Service Hours + Phase 3 Placeholders quickly.
    const rowStyle = item.initialed
        ? { background: "transparent" }
        : priority
            ? { background: "rgba(255, 214, 95, 0.10)", boxShadow: "inset 3px 0 0 var(--color-warning)" }
            : { background: "rgba(40, 216, 224, 0.04)" };

    return (
        <tr
            className="border-b border-[var(--color-line)]"
            style={rowStyle}
            data-testid={`checklist-row-${item.key}`}
        >
            <td className="py-2 pr-2 align-top">
                <div className="flex items-center gap-2 flex-wrap">
                    {priority && !item.initialed ? (
                        <Star size={14} className="text-[var(--color-warning)] shrink-0" />
                    ) : null}
                    <p className="font-semibold m-0">{item.label}</p>
                    {item.initialed ? (
                        <span
                            className="a1-badge text-xs"
                            style={{ background: "rgba(40, 216, 224, 0.12)", color: "var(--color-accent)", border: "1px solid var(--color-accent)" }}
                        >
                            <CheckCircle2 size={10} /> Verified
                        </span>
                    ) : (
                        <span
                            className="a1-badge text-xs"
                            style={{ background: "rgba(231, 119, 78, 0.10)", color: "var(--color-storm)", border: "1px solid var(--color-storm)" }}
                        >
                            <AlertTriangle size={10} /> Pending
                        </span>
                    )}
                </div>
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{item.key}</p>
                <input
                    className="a1-input sm:hidden mt-2"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Verified value"
                    data-testid={`checklist-value-mobile-${item.key}`}
                />
            </td>
            <td className="py-2 pr-2 align-top hidden sm:table-cell">
                <input
                    className="a1-input"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Verified value"
                    data-testid={`checklist-value-${item.key}`}
                />
            </td>
            <td className="py-2 text-center align-top">
                <input
                    type="checkbox"
                    checked={!!item.initialed}
                    onChange={async (e) => {
                        setSaving(true);
                        await onSave(item, value, e.target.checked);
                        setSaving(false);
                    }}
                    disabled={saving}
                    data-testid={`checklist-initial-${item.key}`}
                />
                <p className="text-xs text-[var(--color-text-muted)]">{item.initialed_by ? `${item.initialed_by}` : ""}</p>
            </td>
        </tr>
    );
}
